import { useMutation, useQuery } from "@tanstack/react-query";

import useDispatchStore from "@web-app/store/dispatchStore";
import {
  BROCreateRoutesPayload,
  createRoutePayloadType,
  updateRoutePayloadV2Type,
} from "@web-app/types";
import { logMutationError } from "@web-app/utils/errorHandler";

import { useOrdersCache } from "../cache/orders";
import {
  CopyRoutePayload,
  copy,
  create,
  createBulkRoutes,
  createDraft,
  getRouteDriverRoute,
  remove,
  updateV2,
} from "../services/routes";

export const useRouteMutation = () => {
  const ordersCache = useOrdersCache();

  const { routes } = useDispatchStore();
  const createRouteDraft = useMutation({
    mutationFn: async (payload: createRoutePayloadType) => {
      const response = await createDraft(payload);
      return response;
    },
  });

  const createRoute = useMutation({
    mutationFn: async (payload: createRoutePayloadType) => {
      const response = await create(payload);
      return response;
    },
    onError: (err: unknown) => {
      logMutationError(err, "route creation");
    },
    onSuccess: (data) => {
      ordersCache.invalidateOrdersCache();
      routes.set(data.id, data);

      useDispatchStore.setState({
        focusedRouteId: data.id,
        routes: routes,
        selectedOrderIds: new Set(),
      });
    },
  });

  const updateRouteV2 = useMutation({
    mutationFn: async (payload: updateRoutePayloadV2Type) => {
      const response = await updateV2(payload);
      return response;
    },
    onError: (err: unknown) => {
      logMutationError(err, "route update");
    },
  });

  const removeRoute = useMutation({
    mutationFn: async (routeId: string) => {
      await remove(routeId);
    },
    onSuccess: () => {
      ordersCache.invalidateOrdersCache();

      useDispatchStore.setState({
        focusedRouteId: "",
      });
    },
  });

  const copyRoute = useMutation({
    mutationFn: async ({
      payload,
      routeId,
    }: {
      payload: CopyRoutePayload;
      routeId: string;
    }) => {
      const response = await copy(routeId, payload);
      return response;
    },
    onError: (err: unknown) => {
      logMutationError(err, "route copy");
    },
    onSuccess: () => {
      ordersCache.invalidateOrdersCache();
    },
  });

  const createBulkRoutesFromDrafts = useMutation({
    mutationFn: async (payload: BROCreateRoutesPayload) => {
      const response = await createBulkRoutes(payload);
      return response;
    },
  });

  return {
    copyRoute,
    createBulkRoutesFromDrafts,
    createRoute,
    createRouteDraft,
    removeRoute,
    updateRouteV2,
  };
};

export const useRouteDriverRoute = (routeId: string) => {
  return useQuery({
    enabled: !!routeId,
    queryFn: getRouteDriverRoute,
    queryKey: ["routeDriverRoute", routeId],
    staleTime: 1000 * 60 * 60, // 1 hour
  });
};
